/**
 * Repco Import Slice
 *
 * Manages the Repco SmartQuote → Job work items import flow.
 *
 * State lifecycle:
 *   idle  → previewing  → preview-ready  → committing  → idle (modal closed)
 *                             ↑
 *                          editable
 *
 * The slice owns the preview document so the user can edit it freely between
 * the preview and commit steps without re-running the LLM. Commit returns the
 * full updated Job, which we forward into jobSlice via the existing
 * `selectedJob` slot — same pattern used by addWorkItem.fulfilled.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
    previewRepcoImportApi,
    commitRepcoImportApi,
} from '../../services/repcoImportApi';

// ============================================
// Async Thunks
// ============================================

/**
 * Run the LLM preview. Sends the uploaded CSV file and pasted operations
 * text to the backend, which parses + calls Claude + returns the structured
 * proposal.
 */
export const previewRepcoImport = createAsyncThunk(
    'repcoImport/preview',
    async ({ jobUuid, csvFile, operationsText }, { rejectWithValue }) => {
        try {
            return await previewRepcoImportApi(jobUuid, csvFile, operationsText);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message
                || error.message
                || 'Failed to generate Repco import preview',
            );
        }
    },
);

/**
 * Commit the user-confirmed proposal. The backend creates JobWorkItem rows
 * on the job and returns the full updated Job. We forward that into
 * jobSlice's selectedJob by returning the Job from this thunk — the modal
 * listens for `repcoImport/commit/fulfilled` to refresh the parent view.
 *
 * (We can't import from jobSlice without risking a circular dependency, so
 * the JobDetailModal wrapper dispatches a separate refresh action if needed.
 * In practice we just call onRefreshJob() after fulfillment.)
 */
export const commitRepcoImport = createAsyncThunk(
    'repcoImport/commit',
    async ({ jobUuid, payload }, { rejectWithValue }) => {
        try {
            return await commitRepcoImportApi(jobUuid, payload);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message
                || error.message
                || 'Failed to commit Repco import',
            );
        }
    },
);

// ============================================
// Slice
// ============================================

const initialState = {
    // Modal visibility — owned here rather than locally so the redux devtools
    // show the import flow as a single coherent transaction.
    modalOpen: false,

    // 'idle' | 'previewing' | 'preview-ready' | 'committing'
    phase: 'idle',

    // The proposal returned from /preview. Editable while phase === 'preview-ready'.
    // Shape mirrors the backend PreviewResponse DTO.
    preview: null,

    // Optimistic edits to the proposed work items. When the user edits a
    // labour-hours or part quantity in the preview, we mutate this slice
    // rather than the original `preview` so we can show a "Reset to suggestion"
    // affordance later if we want it.
    editedWorkItems: null,

    error: null,
};

const repcoImportSlice = createSlice({
    name: 'repcoImport',
    initialState,
    reducers: {
        openModal(state) {
            state.modalOpen = true;
            state.phase = 'idle';
            state.preview = null;
            state.editedWorkItems = null;
            state.error = null;
        },
        closeModal(state) {
            state.modalOpen = false;
            state.phase = 'idle';
            state.preview = null;
            state.editedWorkItems = null;
            state.error = null;
        },
        clearError(state) {
            state.error = null;
        },
        /**
         * Update a single field on a single proposed work item. Used by the
         * preview table's inline editors (description, detailNotes,
         * labourHours, labourRate).
         */
        editWorkItemField(state, action) {
            const { tempId, field, value } = action.payload;
            if (!state.editedWorkItems) return;
            const item = state.editedWorkItems.find((w) => w.tempId === tempId);
            if (item) item[field] = value;
        },
        /**
         * Remove a proposed work item entirely (user decided not to import it).
         */
        removeWorkItem(state, action) {
            const tempId = action.payload;
            if (!state.editedWorkItems) return;
            state.editedWorkItems = state.editedWorkItems.filter(
                (w) => w.tempId !== tempId,
            );
        },
        /**
         * Update a part on a proposed work item (quantity / unit price).
         */
        editPartField(state, action) {
            const { tempId, partIndex, field, value } = action.payload;
            if (!state.editedWorkItems) return;
            const item = state.editedWorkItems.find((w) => w.tempId === tempId);
            if (!item || !item.parts || !item.parts[partIndex]) return;
            item.parts[partIndex][field] = value;
        },
        /**
         * Remove a part line from a proposed work item.
         */
        removePart(state, action) {
            const { tempId, partIndex } = action.payload;
            if (!state.editedWorkItems) return;
            const item = state.editedWorkItems.find((w) => w.tempId === tempId);
            if (!item || !item.parts) return;
            item.parts = item.parts.filter((_, i) => i !== partIndex);
        },
    },
    extraReducers: (builder) => {
        builder
            // ==================== Preview ====================
            .addCase(previewRepcoImport.pending, (state) => {
                state.phase = 'previewing';
                state.error = null;
            })
            .addCase(previewRepcoImport.fulfilled, (state, action) => {
                state.phase = 'preview-ready';
                state.preview = action.payload;
                // Deep-clone the proposed items so mutations from the inline
                // editors don't reach back into the immutable preview snapshot.
                state.editedWorkItems = JSON.parse(
                    JSON.stringify(action.payload.proposedWorkItems || []),
                );
            })
            .addCase(previewRepcoImport.rejected, (state, action) => {
                state.phase = 'idle';
                state.error = action.payload;
            })

            // ==================== Commit ====================
            .addCase(commitRepcoImport.pending, (state) => {
                state.phase = 'committing';
                state.error = null;
            })
            .addCase(commitRepcoImport.fulfilled, (state) => {
                state.modalOpen = false;
                state.phase = 'idle';
                state.preview = null;
                state.editedWorkItems = null;
                state.error = null;
            })
            .addCase(commitRepcoImport.rejected, (state, action) => {
                state.phase = 'preview-ready';
                state.error = action.payload;
            });
    },
});

export const {
    openModal,
    closeModal,
    clearError,
    editWorkItemField,
    removeWorkItem,
    editPartField,
    removePart,
} = repcoImportSlice.actions;

export default repcoImportSlice.reducer;